import { useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { translateApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'

// Hidden, not removed. The suggest call and everything around it still work;
// the button is just not shown. Flip this back to true to put it back.
const SHOW_TRANSLATE = false

/**
 * The Tamil name for a catalog row, with the model offering a first draft.
 *
 * The draft is never saved on its own. It lands in the field, marked as a
 * suggestion, and it is stored only when the admin saves the form — because
 * this is the name WhatsApp reads back to a customer, and a wrong Tamil noun
 * on a wire-nail SKU is a wrong delivery nobody catches.
 *
 * Leaving it empty is allowed everywhere: English is the required value.
 */
export default function TamilField({
  value,
  onChange,
  source,
  entityType,
  label = 'Tamil name',
  hint,
  disabled = false,
}) {
  // 'ai' until the admin types into it, which is what the badge reports.
  const [state, setState] = useState(null)
  const [warnings, setWarnings] = useState([])
  const [error, setError] = useState('')
  const inputRef = useRef(null)

  // A different row is being edited: whatever was suggested for the last one
  // says nothing about this one.
  useEffect(() => {
    setState(null)
    setWarnings([])
    setError('')
  }, [entityType, source])

  const suggest = useMutation({
    mutationFn: () => translateApi.suggest(source, entityType),
    onSuccess: (result) => {
      setError('')
      setWarnings(result.warnings ?? [])

      // An answer with no Tamil in it is not a translation — asked for the
      // Tamil of 'Bundles', the model once said '521'. Showing what it said
      // is useful; writing it into the field only means the admin has to
      // clear it before they can type.
      if (result.usable === false) {
        setState(null)
        setError(`The model answered "${result.text}", which is not Tamil. Type it in yourself.`)
        inputRef.current?.focus()
        return
      }

      setState('ai')
      onChange(result.text)
      inputRef.current?.focus()
    },
    onError: (e) => {
      setWarnings([])
      setError(errorMessage(e))
    },
  })

  const ready = Boolean((source || '').trim()) && !disabled

  return (
    <label className="field">
      <span className="tamil-label">
        {label}
        {state === 'ai' && (
          <span className="badge badge-suggested" title="Suggested by the model — check it before saving">
            AI suggestion
          </span>
        )}
      </span>

      <div className="tamil-input">
        <input
          ref={inputRef}
          value={value ?? ''}
          lang="ta"
          disabled={disabled}
          onChange={(e) => {
            // Typed over: it is the admin's words now, whatever it started as.
            setState(null)
            onChange(e.target.value)
          }}
          placeholder="தமிழ் பெயர்"
        />
        {SHOW_TRANSLATE && (
          <button
            type="button"
            className="btn btn-sm"
            disabled={!ready || suggest.isPending}
            title={
              ready
                ? 'Ask the model for a Tamil name'
                : 'Fill in the English name first'
            }
            onClick={() => suggest.mutate()}
          >
            {suggest.isPending ? 'Translating…' : 'Translate'}
          </button>
        )}
      </div>

      {/* Measured on names of identical shape: 0.6 s for one, 119 s for
          another. A disabled button alone reads as broken, and 'a minute'
          read as broken too once it passed a minute. */}
      {suggest.isPending && (
        <span className="hint">
          Asking the model — this can take a couple of minutes.
        </span>
      )}
      {error && <span className="hint caution">{error}</span>}
      {warnings.map((w) => (
        <span key={w} className="hint caution">
          {w}
        </span>
      ))}
      {hint && !error && !warnings.length && <span className="hint">{hint}</span>}
    </label>
  )
}
